import React, { Component } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { withTranslation } from 'react-i18next'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import { muiTheme } from 'cityads-v3/utils/styleUtils'
import i18n from 'cityads-v3/i18n'
import withHooks from 'cityads-v3/decorators/withHooks'
import { useLocalStorageReader, useStoredSettings, useStoredSettingsListener } from 'cityads-v3/hooks/storedSettings'

import Checkbox from '@mui/material/Checkbox'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUpOutlined'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDownOutlined'
import SearchIcon from '@mui/icons-material/SearchOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMoreOutlined'
import ExpandLessIcon from '@mui/icons-material/ExpandLessOutlined'

import { ensureArray, arrayIncludesValue } from 'cityads-v3/utils/arrayUtils'

import classes from './FilterVertical.scss'

export class FilterVerticalCheckbox extends Component {
  static propTypes = {
    title: PropTypes.string,
    icon: PropTypes.string,
    name: PropTypes.string,
    checked: PropTypes.bool,
    onChange: PropTypes.func,
    isIndeterminate: PropTypes.bool,
    count: PropTypes.string,
    disabled: PropTypes.bool,
    image: PropTypes.string,
    textColor: PropTypes.string,
  }

  render() {
    const { title, disabled, checked, onChange, name, value, icon, isIndeterminate, image, count, textColor } = this.props

    const label = count === undefined ? title : (
      <div>{title} <span className={classes.checkboxCount}>({count})</span></div>
    )

    return (
      <FormControlLabel
        control={
          <Checkbox
            ref="checkbox"
            title={title}
            disabled={disabled}
            checked={checked}
            onChange={(evt, set) => onChange(name, value, set, evt)}
            indeterminate={isIndeterminate}
            classes={{
              root: classNames(classes.checkboxRoot, { [classes.checkboxRootWithIcon]: icon })
            }}
            sx={theme => ({
              color: textColor || theme.palette.text.primary,
            })}
          />
        }
        label={
          <Typography component="div" className={classNames(classes.checkboxLabel, { [classes.checkboxLabel__withIcon]: icon || image })}>
            <span style={{ color: textColor }}>{label}</span>
            {icon ?
              (<Icon className={classes.checkboxIcon}>{icon}</Icon>) :
              (image ? <img alt="" className={classes.checkboxImage} src={image} /> : null)
            }
          </Typography>
        }
        classes={{
          root: classes.FormControlLabel,
          label: classes.checkboxLabel
        }}
      />
    )
  }
}

@withHooks(props => {
  const defaultOpened = props.isPrimary ? true : !!props.expanded
  const [open, setOpen] = useStoredSettings('filter_vertical_' + props.name, defaultOpened)
  useLocalStorageReader('filter_vertical_' + props.name)
  useStoredSettingsListener('filter_vertical_' + props.name)

  return {
    ...props,
    open,
    setOpen,
  }
})
class FilterVerticalSection extends Component {
  static propTypes = {
    expanded: PropTypes.bool,
    children: PropTypes.any,
    title: PropTypes.string,
    onToggleExpand: PropTypes.func,
    isPrimary: PropTypes.bool,
    color: PropTypes.string,
    textColor: PropTypes.string,
  };

  toggleExpand() {
    this.props.setOpen(!this.props.open)
    this.props.onToggleExpand && this.props.onToggleExpand()
  }

  render() {
    const { children, title, isPrimary, color, open } = this.props
    const styles = color ? {
      backgroundColor: color,
    } : undefined

    return (
      <div
        style={styles}
        className={classes.filterVerticalSection}
      >
        {isPrimary ? false : (
          <div className={classes.filterVerticalSection__header} onClick={() => this.toggleExpand()}>
            {title}
            <div className={classes.filterVerticalSection__icon}>
              {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </div>
          </div>
        )}
        {open && (
          <div className={classes.filterVerticalSection__content + ' ' + (isPrimary ? classes.filterVerticalSection__content_primary : '')}>
            {children}
          </div>
        )}
      </div>
    )
  }
}

@withTranslation(['main', 'Offers'], { i18n, wait: !!window })
export class MultiGroup extends Component {
  static propTypes = {
    title: PropTypes.string,
    name: PropTypes.string.isRequired,
    items: PropTypes.array,
    value: PropTypes.array,
    onChange: PropTypes.func.isRequired,
    search: PropTypes.bool,
    searchLabel: PropTypes.string,
    expanded: PropTypes.bool,
    sliceItems: PropTypes.number,
    topLevelExpanded: PropTypes.bool,
    onToggleExpand: PropTypes.func,
    isPrimary: PropTypes.bool,
    color: PropTypes.string,
    textColor: PropTypes.string,
    hideEmpty: PropTypes.bool,
  }

  state = {
    search: '',
    itemsPlain: [],
    expanded: false,
  }

  handleSearch(value) {
    this.setState({
      search: value
    })
  }

  UNSAFE_componentWillReceiveProps(props) {
    if (this.props.search && props.items && props.items.length) {
      const itemsPlain = dirToPlain(props.items, {
        children: 'items'
      })

      this.setState({
        itemsPlain
      })
    }
  }

  render() {
    const {
      t,
      items,
      title,
      name,
      value,
      onChange,
      isPrimary,
      disabled,
      expanded,
      topLevelExpanded,
      onToggleExpand,
      color,
      textColor,
      sliceItems,
      hideEmpty,
    } = this.props

    if (hideEmpty && (!items || items.length === 0)) {
      return false
    }

    let content = (
      (this.state.search && this.props.search ?
        this.state.itemsPlain
          .filter(item => item.title.toLowerCase().indexOf(this.state.search.toLowerCase()) !== -1)
        :
        items
      ) || [])
      .map(item => {
        const options = {
          ...item,
          name,
          selected: value,
          onChange,
          disabled,
          expanded: topLevelExpanded,
          onToggleExpand,
          color,
          textColor,
        }
        return <MultiGroupItem key={item.value} {...options} />
      })

    const len = content.length

    if (sliceItems && len > sliceItems) {
      if (!this.state.expanded) {
        content = content.slice(0, sliceItems)
      }
      content.push(
        <Typography
          key="subheader"
          inset={true}
          color="textSecondary"
          className={classes.filterMoreSubheader}
          onClick={() => this.setState({ expanded: !this.state.expanded })}
        >
          {
            this.state.expanded
              ? t('collapseList')
              : t('Offers:filters.moreSubheader', { count: len - sliceItems })
          }
        </Typography>
      )
    }

    const children = (content && content.length) ?
      content.reduce((prev, curr, index) => [
        ...ensureArray(prev),
        <Divider key={'divider' + index} />,
        curr
      ]) :
      false

    return (
      <MuiThemeProvider theme={name === 'tags' ? muiTheme : {}}>
        <FilterVerticalSection
          title={title}
          expanded={expanded}
          onToggleExpand={onToggleExpand}
          isPrimary={isPrimary}
          color={color}
          name={name}
        >
          {isPrimary
            ? (
              <div className={classes.filterVerticalSection__header} style={{ backgroundColor: color, color: textColor }}>
                {children}
              </div>
            )
            :
            (
              <div className={classes.multigroup}>
                {this.props.search && (
                  <TextField
                    fullWidth
                    className={classes.multigroup__search}
                    variant="standard"
                    InputProps={{
                      startAdornment: <SearchIcon color="action" />
                    }}
                    placeholder={this.props.searchLabel || t('search')}
                    onChange={evt => this.handleSearch(evt.target.value)}
                    value={this.state.search || undefined}
                  />
                )}
                {children}
              </div>
            )}
        </FilterVerticalSection>
      </MuiThemeProvider>
    )
  }
}

export class MultiGroupItem extends Component {

  static propTypes = {
    expanded: PropTypes.bool,
    title: PropTypes.string,
    name: PropTypes.string.isRequired,
    icon: PropTypes.string,
    selected: PropTypes.array,
    items: PropTypes.array,
    count: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    onToggleExpand: PropTypes.func,
    textColor: PropTypes.string
  }

  state = {
    expanded: !!this.props.expanded
  }

  toggleExpand() {
    this.setState({
      expanded: !this.state.expanded
    })

    this.props.onToggleExpand && this.props.onToggleExpand()
  }

  render() {

    const checked = arrayIncludesValue(this.props.selected, this.props.value)
    const isIndeterminate = checked ? false : (this.props.items && hasAnySelected(this.props.items, this.props.selected)) || false

    const checkbox = {
      title: this.props.title,
      name: this.props.name,
      value: this.props.value,
      onChange: this.props.onChange,
      count: this.props.count,
      disabled: this.props.disabled,
      checked,
      isIndeterminate,
      icon: (!this.props.items || !this.props.items.length) ? this.props.icon : null,
      image: this.props.image,
      textColor: this.props.textColor
    }

    return (
      <div className={classes.multigroup__item}>
        <div className={classes.multigroup__head} data-item>
          <div className={classes.multigroup__control}>
            <FilterVerticalCheckbox {...checkbox} />
          </div>
          {this.renderExpander()}
        </div>
        {this.renderChildren(checkbox)}
      </div>
    )
  }

  renderExpander() {
    return (this.props.items && this.props.items.length && (
      <div className={classes.multigroup__expander}>
        <IconButton
          classes={{
            root: classes.multigroup__expanderButton
          }}
          onClick={() => this.toggleExpand()}
          size="large"
        >
          {this.state.expanded ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
        </IconButton>
      </div>
    )) || false
  }

  renderChildren(checkbox = {}) {
    return (this.state.expanded && this.props.items && this.props.items.length && (
      <div className={classes.multigroup__children}>
        {this.props.items.map(item => {
          const options = {
            ...item,
            parent: this,
            selected: this.props.selected,
            name: this.props.name,
            onChange: this.props.onChange,
            disabled: this.props.disabled,
            onToggleExpand: this.props.onToggleExpand,
          }
          return <MultiGroupItem key={item.value} {...options} />
        })}
      </div>
    )) || false
  }
}

function hasAnySelected(items, selected = []) {
  const len = items.length
  let i = 0
  let result = false

  for (; i < len; i++) {
    const item = items[i]
    if (arrayIncludesValue(selected, item.value)) {
      result = true
      break
    }

    if (item.items && item.items.length) {
      result = hasAnySelected(item.items, selected)
      if (result) {
        break
      }
    }

  }

  return result
}

function dirToPlain(arr, { children } = {}) {
  let plain = []

  arr.forEach(item => {
    const newItem = { ...item }
    delete newItem[children]
    delete newItem.items
    plain.push(newItem)
    if (item[children] && item[children].length) {
      plain = [
        ...plain,
        ...dirToPlain(item[children], { children })
      ]
    }
  })

  return plain
}
